import { useEffect, useState } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  ArcElement,
  Tooltip,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import {
  fetchGuardrailStats,
  fetchOrdersByHour,
  fetchPopularMenus,
  fetchSalesStats,
} from '../../api/appmanageStats';
import {
  GuardrailMetric,
  GuardrailStats,
  OrdersByHour,
  PaymentMethodStat,
  PopularMenu,
  SalesStat,
} from '../../types/admin';
import './SalesStatsScreen.scss';

// 기획서 가드레일 목표치: 주문 미확인 타임아웃 2% 미만, 결제 오류율 0.1% 미만.
const UNCONFIRMED_TIMEOUT_TARGET = 0.02;
const PAYMENT_ERROR_TARGET = 0.001;

function formatRate(rate: number) {
  return `${(rate * 100).toFixed(2)}%`;
}

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const PAYMENT_METHOD_LABEL: Record<PaymentMethodStat['payment_method'], string> = {
  card: '카드',
  kakao_pay: '카카오페이',
  toss_pay: '토스페이',
};

function SalesStatsScreen() {
  const [range, setRange] = useState<'daily' | 'monthly'>('daily');
  const [totals, setTotals] = useState<SalesStat[]>([]);
  const [byPaymentMethod, setByPaymentMethod] = useState<PaymentMethodStat[]>([]);
  const [ordersByHour, setOrdersByHour] = useState<OrdersByHour[]>([]);
  const [popularMenus, setPopularMenus] = useState<PopularMenu[]>([]);
  const [guardrails, setGuardrails] = useState<GuardrailStats | null>(null);

  useEffect(() => {
    fetchSalesStats(range).then(({ totals, byPaymentMethod }) => {
      setTotals(totals);
      setByPaymentMethod(byPaymentMethod);
    });
  }, [range]);

  useEffect(() => {
    fetchOrdersByHour().then(setOrdersByHour);
    fetchPopularMenus().then(setPopularMenus);
    fetchGuardrailStats().then(setGuardrails);
  }, []);

  return (
    <div className="SalesStatsScreen">
      <div className="SalesStatsScreen-header">
        <h1>매출 및 통계</h1>
        <div className="SalesStatsScreen-toggle">
          <button type="button" className={range === 'daily' ? 'active' : ''} onClick={() => setRange('daily')}>
            일별
          </button>
          <button type="button" className={range === 'monthly' ? 'active' : ''} onClick={() => setRange('monthly')}>
            월별
          </button>
        </div>
      </div>

      {guardrails ? (
        <div className="GuardrailStats">
          <GuardrailCard
            label="주문 미확인 타임아웃"
            description={`최근 ${guardrails.days}일 · ${guardrails.thresholdMinutes}분 초과 기준`}
            metric={guardrails.unconfirmedTimeout}
            target={UNCONFIRMED_TIMEOUT_TARGET}
          />
          <GuardrailCard
            label="결제 오류율"
            description={`최근 ${guardrails.days}일`}
            metric={guardrails.paymentError}
            target={PAYMENT_ERROR_TARGET}
          />
        </div>
      ) : null}

      <div className="SalesStatsScreen-grid">
        <section>
          <h2>{range === 'daily' ? '일별' : '월별'} 매출</h2>
          <Bar
            data={{
              labels: totals.map((t) => t.period),
              datasets: [{ label: '매출(원)', data: totals.map((t) => t.total), backgroundColor: '#5c3d24' }],
            }}
            options={{ responsive: true, plugins: { legend: { display: false } } }}
          />
        </section>

        <section>
          <h2>결제 수단별 매출</h2>
          <Pie
            data={{
              labels: byPaymentMethod.map((p) => PAYMENT_METHOD_LABEL[p.payment_method]),
              datasets: [
                {
                  data: byPaymentMethod.map((p) => p.total),
                  backgroundColor: ['#5c3d24', '#e2b13c', '#2f8f4e'],
                },
              ],
            }}
          />
        </section>

        <section>
          <h2>시간대별 주문 건수</h2>
          <Bar
            data={{
              labels: ordersByHour.map((h) => `${h.hour}시`),
              datasets: [{ label: '주문 건수', data: ordersByHour.map((h) => h.count), backgroundColor: '#e2b13c' }],
            }}
            options={{ responsive: true, plugins: { legend: { display: false } } }}
          />
        </section>

        <section>
          <h2>인기 메뉴 순위</h2>
          <ol className="PopularMenuList">
            {popularMenus.map((menu) => (
              <li key={menu.menu_name}>
                <span>{menu.menu_name}</span>
                <span>{menu.total_quantity}개</span>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}

function GuardrailCard({
  label,
  description,
  metric,
  target,
}: {
  label: string;
  description: string;
  metric: GuardrailMetric;
  target: number;
}) {
  const withinTarget = metric.rate <= target;
  return (
    <div className={`GuardrailCard ${withinTarget ? 'ok' : 'alert'}`}>
      <p className="GuardrailCard-label">{label}</p>
      <p className="GuardrailCard-rate">{formatRate(metric.rate)}</p>
      <p className="GuardrailCard-detail">
        {metric.count}건 / {metric.total}건 · 목표 {formatRate(target)} 미만
      </p>
      <p className="GuardrailCard-description">{description}</p>
    </div>
  );
}

export default SalesStatsScreen;
